import React from 'react';
import styled from 'styled-components';
import { Building2, Layers, TrendingUp, Wallet } from 'lucide-react';
import { DepartmentBreakdownItem } from '../../../services/dashboardService';

interface DepartmentBreakdownWidgetProps {
  departments: DepartmentBreakdownItem[];
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;

  @media (max-width: 767px) {
    padding: 16px;
    border-radius: 12px;
    margin-bottom: 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 20px 0;
`;

const DeptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const DeptRow = styled.div`
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 18px 20px;
`;

const DeptTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const DeptName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const DeptIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Chip = styled.div<{ $tone: 'neutral' | 'success' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: white;
  border: 1px solid var(--border-color);
  color: ${props => (props.$tone === 'success' ? 'var(--success-color)' : 'var(--text-primary)')};
  ${props => props.$tone === 'success' && 'filter: saturate(0.7) brightness(0.9);'}

  svg {
    color: ${props => (props.$tone === 'success' ? 'var(--success-color)' : 'var(--text-secondary)')};
  }
`;

const RevenueValue = styled.span`
  direction: ltr;
`;

const Bars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: 56px 1fr 40px;
  align-items: center;
  gap: 12px;

  @media (max-width: 767px) {
    grid-template-columns: 46px 1fr 36px;
    gap: 8px;
  }
`;

const BarLabel = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const BarTrack = styled.div`
  height: 10px;
  background: var(--border-color);
  border-radius: 5px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $percentage: number; $color: string }>`
  height: 100%;
  width: ${props => Math.min(props.$percentage, 100)}%;
  background: ${props => props.$color};
  border-radius: 5px;
  transition: width 0.3s ease;
  filter: saturate(0.7) brightness(0.9);
`;

const BarValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  text-align: left;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-secondary);
  font-size: 14px;
`;

const formatCurrency = (amount: number): string => {
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const DepartmentBreakdownWidget: React.FC<DepartmentBreakdownWidgetProps> = ({ departments }) => {
  // סקאלה משותפת לכל המחלקות — אורכי העמודות ניתנים להשוואה ישירה
  const globalMax = Math.max(
    1,
    ...departments.map(d => d.leadsCount),
    ...departments.map(d => d.clientsCount)
  );

  return (
    <Container>
      <SectionHeader>
        <SectionIcon>
          <Layers size={20} />
        </SectionIcon>
        <SectionTitle>פילוח לפי מחלקות</SectionTitle>
      </SectionHeader>
      <Subtitle>התפלגות לידים ותיקים, שיעור המרה וחיובים (בניכוי הנחות, כולל מע"מ) לפי מחלקה</Subtitle>

      {departments.length === 0 ? (
        <EmptyState>אין נתונים להצגה</EmptyState>
      ) : (
        <DeptList>
          {departments.map(dept => (
            <DeptRow key={dept.departmentId ?? 'none'}>
              <DeptTop>
                <DeptName>
                  <DeptIcon>
                    <Building2 size={18} />
                  </DeptIcon>
                  {dept.departmentName}
                </DeptName>
                <Chips>
                  <Chip $tone="neutral">
                    <TrendingUp size={14} />
                    שיעור המרה {dept.conversionRate.toFixed(1)}%
                  </Chip>
                  {dept.revenue != null && (
                    <Chip $tone="success">
                      <Wallet size={14} />
                      <RevenueValue>{formatCurrency(dept.revenue)}</RevenueValue>
                    </Chip>
                  )}
                </Chips>
              </DeptTop>

              <Bars>
                <BarRow>
                  <BarLabel>לידים</BarLabel>
                  <BarTrack>
                    <BarFill
                      $percentage={(dept.leadsCount / globalMax) * 100}
                      $color="var(--primary-color)"
                    />
                  </BarTrack>
                  <BarValue>{dept.leadsCount}</BarValue>
                </BarRow>
                <BarRow>
                  <BarLabel>תיקים</BarLabel>
                  <BarTrack>
                    <BarFill
                      $percentage={(dept.clientsCount / globalMax) * 100}
                      $color="var(--success-color)"
                    />
                  </BarTrack>
                  <BarValue>{dept.clientsCount}</BarValue>
                </BarRow>
              </Bars>
            </DeptRow>
          ))}
        </DeptList>
      )}
    </Container>
  );
};

export default DepartmentBreakdownWidget;
